import { Drawer } from '@arkestro/arkestro-design-system';
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { Dayjs } from 'dayjs';
import { REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import { UserContext } from '@components/providers/UserProvider/UserProvider';
import { buildDefaultRequestParams } from '@utils/BidRequestDefault';
import { BidRequestData, BiddingEventRound } from 'components/RfxDrawers/api';
import { alertMessage } from '@utils/common';
import { useSpendCategories } from '@components/RfxDrawers/hooks/useSpendCategories';
import { useCreateNewRfx } from '@components/RfxDrawers/hooks/useCreateNewRfx';
import { Footer, OverviewPanel, PriceAgreementPanel, RecurrencePanel } from '@components/RfxDrawers/shared';
import { useGetCompanyTags } from '@hooks/useGetCompanyTags';
import { useCreateEventSeries } from '@hooks/useEventSeries';
import { EventSeriesCadenceUnit } from 'types/EventSeries';
import { PriceUpdateBusinessUnitField } from '@features/price-update-business-unit';
import { PriceUpdateSchedulePanel } from './PriceUpdateSchedulePanel';
import {
  DEFAULT_SCHEDULE_PERIOD_UNIT,
  deriveSchedule,
  isPastDate,
  SchedulePeriodUnit,
} from './schedulePeriod';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

interface PriceUpdateFormProps {
  businessUnitId: number | null;
  cadence: number | null;
  cadenceUnit: EventSeriesCadenceUnit;
  eventDescription: string;
  eventTitle: string;
  initialSubmissionUnit: SchedulePeriodUnit;
  initialSubmissionValue: number | null;
  isTemplate: boolean;
  isTest: boolean;
  priceAgreementId: number | null;
  pricesDueBy: Dayjs | null;
  reviewUnit: SchedulePeriodUnit;
  reviewValue: number | null;
  tagNames: string[];
}

interface PriceUpdateDrawerProps {
  isOpen: boolean;
  lockPriceAgreement?: boolean;
  onClose: () => void;
  priceAgreementId?: number | null;
}

const DEFAULT_FORM_VALUES: PriceUpdateFormProps = {
  businessUnitId: null,
  cadence: null,
  cadenceUnit: 'days',
  eventDescription: '',
  eventTitle: '',
  initialSubmissionUnit: DEFAULT_SCHEDULE_PERIOD_UNIT,
  initialSubmissionValue: null,
  isTemplate: false,
  isTest: false,
  priceAgreementId: null,
  pricesDueBy: null,
  reviewUnit: DEFAULT_SCHEDULE_PERIOD_UNIT,
  reviewValue: null,
  tagNames: [],
};

/**
 * Round length in whole days, replacing the 7-day default_round values that would otherwise
 * contradict the derived times — the Schedule tab recomputes end_time from length_value, so a
 * stale length silently rewrites the schedule.
 */
const roundLength = (start: Dayjs, end: Dayjs) => ({
  length_unit: 'days',
  length_value: end.diff(start, 'day'),
});

/** Round types read from the company config, falling back for configs predating the review round. */
const PRICING_ROUND_TYPE = 'pricing';
const REVIEW_ROUND_TYPE = 'review';

export const PriceUpdateDrawer = ({ isOpen, lockPriceAgreement = false, onClose, priceAgreementId = null }: PriceUpdateDrawerProps) => {
  const { t } = useTranslation('common');

  const user = useContext(UserContext);
  const router = useRouter();
  const { enablePriceUpdate = false } = useFlags();
  const { data: spendCategories, isLoading: isSpendCategoriesLoading } = useSpendCategories();
  const { data: companyTags = [] } = useGetCompanyTags({ companyId: user?.company?.id, enabled: isOpen });
  const { mutate, isPending: isSavingNewPriceUpdate } = useCreateNewRfx();
  const { mutate: createEventSeries } = useCreateEventSeries();
  const disableFields = isSpendCategoriesLoading || isSavingNewPriceUpdate;
  const configRoundTypes = user?.global?.bidding_events?.round_types;
  const roundTypes = {
    pricing: configRoundTypes?.pricing ?? PRICING_ROUND_TYPE,
    review: configRoundTypes?.review ?? REVIEW_ROUND_TYPE,
  };

  const methods = useForm<PriceUpdateFormProps>({
    defaultValues: { ...DEFAULT_FORM_VALUES, priceAgreementId },
  });

  const { handleSubmit, formState: { isValid }, reset, setValue } = methods;

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && priceAgreementId != null) {
      setValue('priceAgreementId', priceAgreementId, { shouldValidate: true });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, priceAgreementId, setValue]);

  if (!enablePriceUpdate) return null;

  const buildSchedule = (data: PriceUpdateFormProps, defaultRound: BiddingEventRound) => {
    const schedule = deriveSchedule({ ...data, shouldSkipWeekends: true });
    if (!schedule) return null;

    const { endTime, startTime, submissionEnd } = schedule;

    return {
      endTime,
      rounds: [
        {
          ...defaultRound,
          ...roundLength(startTime, submissionEnd),
          end_time: submissionEnd,
          name: t('bidding_event.submission_round_with_num', { num: 1 }),
          number: 1,
          round_type: roundTypes.pricing,
          start_time: startTime,
        },
        {
          ...defaultRound,
          ...roundLength(submissionEnd, endTime),
          end_time: endTime,
          name: t('rfx_drawer.review_round'),
          number: 2,
          round_type: roundTypes.review,
          start_time: submissionEnd,
        },
      ],
      startTime,
    };
  };

  const onSubmit = (data: PriceUpdateFormProps) => {
    const defaultParams = buildDefaultRequestParams(user, REQUEST_TYPE_PRICE_UPDATE) as BidRequestData;
    if (!defaultParams.bidding_event_attributes) return;
    const defaultRound = defaultParams.bidding_event_attributes.rounds_attributes[0];

    const schedule = buildSchedule(data, defaultRound);
    if (!schedule) return;

    const { endTime, rounds, startTime } = schedule;
    if (isPastDate(startTime)) {
      alertMessage(t('rfx_drawer.start_date_in_past_error'), 'error', 10);
      return;
    }

    const params = {
      ...defaultParams,
      bidding_event_attributes: {
        ...defaultParams.bidding_event_attributes,
        end_at: endTime,
        round_count: rounds.length,
        rounds_attributes: rounds,
        start_at: startTime,
      },
      business_unit_id: data.businessUnitId,
      description: data.eventDescription,
      end_time: endTime,
      is_test: data.isTest,
      name: data.eventTitle,
      start_time: startTime,
      tag_names: data.tagNames,
      template: data.isTemplate,
    };

    mutate(params, {
      onError: () => {
        alertMessage(t('rfx_drawer.creation_error'), 'error', 10);
      },
      onSuccess: responseData => {
        const requestId = responseData.bid_request.id;

        createEventSeries({
          bid_request_id: requestId,
          cadence: data.cadence as number,
          cadence_unit: data.cadenceUnit,
          initial_submission_unit: data.initialSubmissionUnit,
          initial_submission_value: data.initialSubmissionValue ?? undefined,
          latest_prices_due_by: data.pricesDueBy?.toISOString(),
          name: data.eventTitle,
          price_agreement_id: data.priceAgreementId,
          review_unit: data.reviewUnit,
          review_value: data.reviewValue ?? undefined,
        }, {
          onError: () => {
            alertMessage(t('rfx_drawer.event_series_creation_error'), 'error', 10);
          },
          onSuccess: () => {
            onClose();
            reset(DEFAULT_FORM_VALUES);
            router.push(`/bid_requests/${requestId}/dashboard`);
          },
        });
      }
    });
  };

  return (
    <Drawer
      classNames={{ body: isSpendCategoriesLoading ? undefined : sharedStyles.drawerBody, footer: sharedStyles.drawerFooter, header: sharedStyles.drawerHeader }}
      footer={<Footer isSaving={isSavingNewPriceUpdate} isValid={isValid} onClose={onClose} onSubmit={handleSubmit(onSubmit)} type='Price Update' />}
      loading={isSpendCategoriesLoading}
      onClose={onClose}
      open={isOpen}
      title={t('rfx_drawer.price_update_title')}
      width={680}
    >
      <FormProvider {...methods}>
        <form>
          <PriceAgreementPanel locked={lockPriceAgreement} />
          <OverviewPanel
            businessUnitField={<PriceUpdateBusinessUnitField />}
            canManageTags
            companyTags={companyTags}
            disableFields={disableFields}
            showTags
            showTemplateToggle={false}
            spendCategories={spendCategories || []}
          />
          <PriceUpdateSchedulePanel disableFields={disableFields} />
          <RecurrencePanel />
        </form>
      </FormProvider>
    </Drawer>
  );
};
